import {HoistModel, LoadSpec, lookup, managed, XH} from '@xh/hoist/core';
import {DashViewModel} from '@xh/hoist/desktop/cmp/dash';
import {Icon} from '@xh/hoist/icon';
import {action, bindable, observable, observableRef, runInAction} from '@xh/hoist/mobx';
import {EconomicEvent, EconomicEventTypeEnum} from '../../../../data/DataTypes';
import {Column, GridModel} from '@xh/hoist/cmp/grid';
import {GridOptions} from 'ag-grid-community';
import {wait} from '@xh/hoist/promise';
import {getDefaultOptions} from '../../../../../core/ServiceUtils';
import {flagRenderer} from './EconomicCalendarWidget';
import {fmtDate, fmtDateTime, fmtMillions, fmtNumber, fmtPercent, fmtThousands} from '@xh/hoist/format';
import {secExchangeBadge} from '../../../../cmp/secBadges/SecBadges';
import {hbox} from '@xh/hoist/cmp/layout';
import {tickerPopup} from '../../../portfolio/impl/positions/TickerPanel';
import {Timer} from '@xh/hoist/utils/async';
import {MINUTES} from '@xh/hoist/utils/datetime';
import {ReactNode} from 'react';
import {sortBy, uniqBy} from 'lodash';

export class EconomicCalendarWidgetModel extends HoistModel {
    @observable accessor type: EconomicEventTypeEnum;
    @lookup(DashViewModel) dashViewModel: DashViewModel;
    @managed gridModel = this.createGridModel();
    @managed private timer = Timer.create({interval: -1, runFn: () => this.loadAsync()});

    @bindable accessor count = -1;
    @bindable accessor country: string = null;
    @bindable accessor frequency: string = null;
    @bindable accessor exchange: string = null;
    @bindable accessor splitType: string = null;

    @observableRef accessor countryOptions: {value: string; label: string}[] = [];
    @observableRef accessor frequencyOptions: {value: string; label: string}[] = [];
    @observableRef accessor exchangeOptions: {value: string; label: string}[] = [];
    @observableRef accessor splitTypeOptions: {value: string; label: string}[] = [];

    override onLinked() {
        this.markPersist('type', {dashViewModel: this.dashViewModel});
        this.markPersist('count', {dashViewModel: this.dashViewModel});

        this.dashViewModel.extraMenuItems.push({
            icon: Icon.gear(),
            text: 'Configure',
            items: Object.values(EconomicEventTypeEnum).map(type => ({
                text: type,
                key: type,
                actionFn: () => this.setType(type)
            }))
        });

        this.setColumns();

        wait().then(async () => {
            await this.gridModel.whenReadyAsync();
            this.addReaction({
                track: () => this.count,
                run: () => this.updatePageSize()
            });
        });

        this.addReaction({
            track: () => [this.country, this.frequency, this.exchange, this.splitType],
            run: () => this.applyFilter()
        });
    }

    override async doLoadAsync(loadSpec: LoadSpec): Promise<void> {
        const {type} = this;
        if (!type) return;

        this.gridModel.clear();

        let data: EconomicEvent[] = await XH.fetchService.fetchJson({
            url: `${XH.getConf('svcApiUrl')}/economics/eventCalendar/${type}`,
            method: 'GET',
            ...getDefaultOptions()
        });

        data = uniqBy(data, 'symbol');

        data.forEach(d => {
            if (d.countryCode == 'UK') d.countryCode = 'GB';
        });

        this.gridModel.loadData(data.map(d => ({...d, type})));

        runInAction(() => {
            this.countryOptions = this.buildOptions(data, 'countryCode');
            this.frequencyOptions = this.buildOptions(data, 'frequency');
            this.exchangeOptions = this.buildOptions(data, 'exchange');
            this.splitTypeOptions = this.buildOptions(data, 'splitType');
        });
    }

    @action
    private setType(type: EconomicEventTypeEnum) {
        this.type = type;
        this.country = null;
        this.frequency = null;
        this.exchange = null;
        this.splitType = null;
        this.setColumns();

        this.dashViewModel.title = type;
        this.loadAsync();

        this.timer.setInterval(type == EconomicEventTypeEnum.Economic ? 2 * MINUTES : 30 * MINUTES);
    }

    get agOptions(): GridOptions {
        return {
            pagination: true,
            suppressPaginationPanel: true,
            paginationPageSize: this.count === -1 ? 0 : this.count,
            paginationAutoPageSize: this.count === -1
        };
    }

    private updatePageSize() {
        if (!this.gridModel.isReady) return;
        if (this.count === -1) {
            this.gridModel.agApi.setGridOption('paginationAutoPageSize', true);
        } else {
            this.gridModel.agApi.setGridOption('paginationAutoPageSize', false);
            this.gridModel.agApi.setGridOption('paginationPageSize', this.count);
        }
    }

    private applyFilter() {
        const {type} = this;
        if (type == EconomicEventTypeEnum.Economic && this.country) this.gridModel.store.setFilter({field: 'countryCode', op: '=', value: this.country});
        else if (type == EconomicEventTypeEnum.Dividends && this.frequency)
            this.gridModel.store.setFilter({field: 'frequency', op: '=', value: this.frequency});
        else if (type == EconomicEventTypeEnum.IPOs && this.exchange) this.gridModel.store.setFilter({field: 'exchange', op: '=', value: this.exchange});
        else if (type == EconomicEventTypeEnum.Splits && this.splitType) this.gridModel.store.setFilter({field: 'splitType', op: '=', value: this.splitType});
        else this.gridModel.store.clearFilter();
    }

    private buildOptions(data: EconomicEvent[], field: string): {value: string; label: string}[] {
        const values = [...new Set(data.map(d => d[field]).filter(Boolean))];
        return sortBy(
            values.map(v => ({value: v, label: v})),
            'label'
        );
    }

    private setColumns() {
        let visibleColumns: string[];
        if (this.type == EconomicEventTypeEnum.Economic) visibleColumns = ['date', 'countryCode', 'event', 'eventValue'];
        else if (this.type == EconomicEventTypeEnum.Dividends) visibleColumns = ['symbol', 'recordDate', 'dividend', 'frequency'];
        else if (this.type == EconomicEventTypeEnum.Earnings) visibleColumns = ['symbol', 'date', 'eps', 'revenue'];
        else if (this.type == EconomicEventTypeEnum.IPOs) visibleColumns = ['symbol', 'date', 'company', 'exchange'];
        else if (this.type == EconomicEventTypeEnum.Splits) visibleColumns = ['symbol', 'date', 'split', 'splitType'];

        this.gridModel.columns.forEach((c: Column) => this.gridModel.setColumnVisible(c.colId, visibleColumns.includes(c.colId)));
    }

    private createGridModel(): GridModel {
        return new GridModel({
            store: {
                idSpec: 'symbol'
            },
            columns: [
                {field: 'type', hidden: true},
                {
                    field: 'symbol',
                    width: 80,
                    renderer: (v, {record}) =>
                        tickerPopup({
                            // @ts-ignore - partial position used intentionally for popup context
                            position: {ticker: v}
                        })
                },
                {
                    field: 'date',
                    width: 160,
                    renderer: (v, {record}) => (record.data.type == EconomicEventTypeEnum.Economic ? fmtDateTime(v) : fmtDate(v))
                },
                {
                    field: 'countryCode',
                    headerName: null,
                    align: 'center',
                    width: 40,
                    renderer: v => flagRenderer({countryCode: v, svg: true}),
                    tooltip: true,
                    cellClass: 'country-cell'
                },
                {field: 'event', tooltip: true, width: 250},
                {
                    field: 'eventValue',
                    headerName: 'Projection',
                    width: 90,
                    renderer: (v, {record}) => {
                        let value: ReactNode;
                        let change = 0;
                        if (record.data.eventUnit == '%') value = fmtPercent(v / 100, {colorSpec: false});
                        else if (record.data.eventUnit == 'K') value = fmtThousands(v * 1000);
                        else if (record.data.eventUnit == 'M') value = fmtMillions(v * 1000000);
                        else if (record.data.eventUnit == 'T') value = fmtNumber(v, {label: 'T'});
                        else value = v;

                        if (record.data.previousValue) {
                            change = v - record.data.previousValue;
                        }

                        return hbox({
                            className: change < 0 ? 'negative-value' : v > 0 ? 'positive-value' : null,
                            justifyContent: 'right',
                            alignItems: 'center',
                            items: [value, change < 0 ? Icon.arrowDown() : change > 0 ? Icon.arrowUp() : Icon.placeholder()]
                        });
                        //span({
                        //    item: `${v}${record.data.eventUnit}`,
                        //    className: v < 0 ? 'negative-value' : 'positive-value'
                        //})
                    }
                },
                {field: 'eventUnit', hidden: true},
                {field: 'previousValue', hidden: true},
                {field: 'recordDate', width: 120, renderer: v => fmtDate(v)},
                {field: 'paymentDate', width: 120, renderer: v => fmtDate(v)},
                {field: 'dividend', align: 'right', width: 80, renderer: v => fmtPercent(v)},
                {field: 'frequency', width: 100},
                {
                    field: 'eps',
                    headerName: 'EPS',
                    align: 'right',
                    width: 80,
                    renderer: v => fmtPercent(v, {colorSpec: true})
                },
                {
                    field: 'revenue',
                    headerName: 'Revenue (MM)',
                    align: 'right',
                    width: 100,
                    renderer: v => fmtMillions(v, {precision: 1, prefix: '$'})
                },
                {field: 'company', tooltip: true, width: 200},
                {field: 'exchange', width: 100, renderer: v => secExchangeBadge(v)},
                {field: 'split', width: 100},
                {field: 'splitType', width: 100}
            ]
        });
    }
}
